import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import api, { rupiah, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GraduationCap, RefreshCw, Sparkles, ArrowRight, BookOpen, CheckCircle2, Circle, ExternalLink } from "lucide-react";

const diffStyle = { Tinggi: "bg-red-100 text-red-800 border-red-300", Sedang: "bg-amber-100 text-amber-800 border-amber-300", Rendah: "bg-slate-100 text-slate-600 border-slate-300" };

export default function CaseStudy() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/case-study").then((r) => setData(r.data)).catch(() => setData({ seeded: false }));
  useEffect(() => { load(); api.get("/meta/case-study").then((r) => setMeta(r.data)); }, []);

  const seed = async () => { setBusy(true); try { const r = await api.post("/case-study/seed"); toast.success(r.data.already ? "Studi kasus sudah ada" : "Studi kasus dibuat"); load(); } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); } };
  const reset = async () => {
    if (!window.confirm("Reset Mode Pelatihan? Semua data studi kasus dikembalikan ke kondisi awal.")) return;
    setBusy(true); try { await api.post("/case-study/reset"); toast.success("Studi kasus di-reset"); load(); } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const openIssue = (it) => { if (data?.engagement_id) navigate(`/engagements/${data.engagement_id}?tab=${it.tab}`); };

  return (
    <Layout>
      <PageHeader breadcrumb="Pelatihan" title="Studi Kasus Audit"
        subtitle={meta ? `${meta.engagement_name}` : "Perikatan pelatihan interaktif"}
        actions={
          <div className="flex gap-2">
            {data?.seeded
              ? <Button size="sm" variant="outline" onClick={reset} disabled={busy} data-testid="cs-reset"><RefreshCw size={14} className="mr-1" />Reset (Mode Pelatihan)</Button>
              : <Button size="sm" onClick={seed} disabled={busy} data-testid="cs-seed"><Sparkles size={14} className="mr-1" />Buat Studi Kasus</Button>}
            {data?.seeded && <Button size="sm" variant="outline" onClick={() => navigate("/case-study/walkthrough")} data-testid="cs-walkthrough"><GraduationCap size={14} className="mr-1" />Walkthrough Terpandu</Button>}
            {data?.seeded && <Button size="sm" onClick={() => navigate(`/engagements/${data.engagement_id}`)} data-testid="cs-open-eng">Buka Perikatan<ArrowRight size={14} className="ml-1" /></Button>}
          </div>} />

      <div className="p-6 space-y-6" data-testid="case-study-page">
        {/* Guide */}
        <div className="bg-slate-900 text-slate-100 rounded-sm p-5" data-testid="cs-guide">
          <div className="flex items-center gap-2 mb-2 font-head font-semibold"><BookOpen size={16} />Panduan Studi Kasus</div>
          <ul className="space-y-1 text-[13px] text-slate-300">
            {(meta?.guide || []).map((g, i) => <li key={i} className="flex gap-2"><span className="text-slate-500">{i + 1}.</span>{g}</li>)}
          </ul>
        </div>

        {!data?.seeded && (
          <div className="bg-white border border-border rounded-sm p-10 text-center" data-testid="cs-empty">
            <GraduationCap size={40} className="mx-auto text-slate-400 mb-3" />
            <div className="font-head text-lg font-semibold mb-1">Studi kasus belum dibuat</div>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto mb-4">Tekan "Buat Studi Kasus" untuk membuat perikatan pelatihan {meta?.client} dengan data realistis (TB, GL, aging, persediaan, register, bank, PPN, aset tetap) dan 10 isu audit yang ditanam.</p>
            <Button onClick={seed} disabled={busy} data-testid="cs-seed-2"><Sparkles size={14} className="mr-1" />Buat Studi Kasus</Button>
          </div>
        )}

        {data?.seeded && (
          <>
            {/* Progress map */}
            <div>
              <div className="text-xs font-head font-semibold uppercase text-muted-foreground mb-2">Peta Progres Audit</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="cs-progress">
                {Object.entries(data.progress || {}).map(([stage, done]) => (
                  <div key={stage} className={`border rounded-sm p-3 flex items-center gap-2 ${done ? "bg-green-50 border-green-300" : "bg-white border-border"}`}>
                    {done ? <CheckCircle2 size={16} className="text-green-600" /> : <Circle size={16} className="text-slate-400" />}
                    <span className="text-xs font-medium">{stage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Materiality banner */}
            <div className="bg-white border border-border rounded-sm p-3 flex flex-wrap gap-6 text-sm" data-testid="cs-materiality">
              <div><span className="text-muted-foreground">Materialitas Keseluruhan: </span><b className="tabular">{rupiah(data.materiality?.overall_materiality)}</b></div>
              <div><span className="text-muted-foreground">Materialitas Pelaksanaan: </span><b className="tabular">{rupiah(data.materiality?.performance_materiality)}</b></div>
              <div><span className="text-muted-foreground">Ambang Trivial: </span><b className="tabular">{rupiah(data.materiality?.clearly_trivial)}</b></div>
              <div><span className="text-muted-foreground">Mata Uang: </span><b>IDR</b></div>
            </div>

            {/* Issues */}
            <div>
              <div className="text-xs font-head font-semibold uppercase text-muted-foreground mb-2">10 Isu Audit yang Ditanam</div>
              <div className="grid md:grid-cols-2 gap-3" data-testid="cs-issues">
                {(data.issues || []).map((it) => (
                  <div key={it.code} className="bg-white border border-border rounded-sm p-4" data-testid={`cs-issue-${it.code}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 border rounded-sm bg-slate-100">{it.code}</span>
                        <span className="text-sm font-semibold">{it.title}</span>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${diffStyle[it.difficulty]}`}>{it.difficulty}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{it.area}{it.amount ? ` · ${rupiah(it.amount)}` : ""}</div>
                    <p className="text-xs mt-2"><span className="font-medium text-red-700">Cacat: </span>{it.deliberate_defect}</p>
                    <p className="text-xs mt-1"><span className="font-medium text-green-700">Diharapkan: </span>{it.expected_finding}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {it.standards.map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 border rounded-sm bg-indigo-50 text-indigo-700 border-indigo-200">{s}</span>)}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] text-muted-foreground">{it.identified ? "✓ Ditangani (AJE/kesimpulan)" : it.status}</span>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openIssue(it)} data-testid={`cs-open-${it.code}`}>Buka di Audit OS<ExternalLink size={12} className="ml-1" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Standards mapping */}
            <div>
              <div className="text-xs font-head font-semibold uppercase text-muted-foreground mb-2">Pemetaan Standar (Dinamis)</div>
              <div className="bg-white border border-border rounded-sm overflow-x-auto" data-testid="cs-standards">
                <table className="w-full audit-table">
                  <thead><tr><th>Standar / Referensi</th><th>Isu Terkait</th></tr></thead>
                  <tbody>{Object.entries(data.standards_map || {}).map(([std, codes]) => (
                    <tr key={std}><td className="text-xs font-medium">{std}</td><td className="text-xs">{codes.join(", ")}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
